import { useState } from "react";
import { toast, Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/common/PageHero";
import { IMAGES } from "@/data/site";
import { api } from "@/lib/api";

function Field({ label, name, value, onChange, type = "text", testId, required = false, options }) {
  return (
    <div>
      <label className="legacy-label">{label}{required && " *"}</label>
      {options ? (
        <select data-testid={testId} required={required} className="legacy-input" name={name} value={value} onChange={onChange}>
          <option value="">Selecionar</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea data-testid={testId} required={required} rows={4} className="legacy-input" name={name} value={value} onChange={onChange} />
      ) : (
        <input data-testid={testId} required={required} type={type} className="legacy-input" name={name} value={value} onChange={onChange} />
      )}
    </div>
  );
}

const emptyClub = {
  club_name: "", responsible_name: "", role: "", email: "", phone: "", country: "",
  position_wanted: "", age_group: "", dominant_foot: "", characteristics: "", deadline: "", notes: "",
};
const emptyPartnership = {
  name: "", organization: "", role: "", country: "", email: "", phone: "",
  partnership_type: "", proposal_description: "", message: "",
};

export default function ClubsPartners() {
  const [club, setClub] = useState(emptyClub);
  const [partnership, setPartnership] = useState(emptyPartnership);
  const [loadingClub, setLoadingClub] = useState(false);
  const [loadingPartner, setLoadingPartner] = useState(false);

  const submitClub = async (e) => {
    e.preventDefault();
    setLoadingClub(true);
    try {
      await api.post("/club-requests", club);
      toast.success("Pedido enviado com sucesso. Entraremos em contacto assim que possível.");
      setClub(emptyClub);
    } catch { toast.error("Ocorreu um erro. Tente novamente."); }
    finally { setLoadingClub(false); }
  };

  const submitPartner = async (e) => {
    e.preventDefault();
    setLoadingPartner(true);
    try {
      await api.post("/partnerships", partnership);
      toast.success("Proposta enviada com sucesso.");
      setPartnership(emptyPartnership);
    } catch { toast.error("Ocorreu um erro. Tente novamente."); }
    finally { setLoadingPartner(false); }
  };

  return (
    <>
      <Toaster theme="dark" position="top-center" />
      <PageHero
        eyebrow="Clubes e parceiros"
        title={<>Um parceiro estratégico na <br /><span className="text-[#A67C00]">identificação de talento</span></>}
        subtitle="Trabalhamos com clubes, academias, treinadores, departamentos de scouting, agentes e organizações desportivas."
        image={IMAGES.stadiumNight}
      />

      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16">
          <div className="reveal">
            <div className="section-eyebrow mb-6">O que oferecemos</div>
            <ul className="space-y-3">
              {["Jogadores previamente avaliados","Relatórios técnicos","Pesquisa de atletas","Análise de compatibilidade com o perfil pretendido","Monitorização de jogadores","Informação estruturada","Comunicação profissional","Apoio no contacto inicial"].map((t, i) => (
                <li key={t} className="flex items-start gap-4 border-b border-white/5 pb-3 text-neutral-300 text-sm">
                  <span className="font-cinzel text-[#A67C00] text-xs">{String(i+1).padStart(2,"0")}</span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal">
            <div className="section-eyebrow mb-6">Como funciona</div>
            <ol className="space-y-6">
              {["O clube envia o perfil pretendido","A agência analisa o pedido","São selecionados jogadores compatíveis","São apresentados os perfis","A agência acompanha o contacto"].map((t, i) => (
                <li key={t} className="flex gap-6 items-start">
                  <span className="font-cinzel text-[#A67C00] text-3xl md:text-4xl">{String(i+1).padStart(2,"0")}</span>
                  <span className="text-neutral-200 text-base pt-2">{t}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* PEDIDO DE JOGADOR */}
      <section id="pedido" className="py-24 md:py-32 bg-[#0F0F0F] border-b border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="section-eyebrow mb-6">Formulário</div>
          <h2 className="font-cinzel uppercase text-white text-3xl md:text-5xl tracking-tight mb-12">Pedido de jogador</h2>
          <form data-testid="club-request-form" onSubmit={submitClub} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Nome do clube / organização" name="club_name" required value={club.club_name} onChange={(e)=>setClub({...club,club_name:e.target.value})} testId="cr-club-name" />
            <Field label="Nome do responsável" name="responsible_name" required value={club.responsible_name} onChange={(e)=>setClub({...club,responsible_name:e.target.value})} testId="cr-responsible" />
            <Field label="Cargo" name="role" value={club.role} onChange={(e)=>setClub({...club,role:e.target.value})} testId="cr-role" />
            <Field label="Email" type="email" required name="email" value={club.email} onChange={(e)=>setClub({...club,email:e.target.value})} testId="cr-email" />
            <Field label="Telefone" name="phone" value={club.phone} onChange={(e)=>setClub({...club,phone:e.target.value})} testId="cr-phone" />
            <Field label="País" name="country" value={club.country} onChange={(e)=>setClub({...club,country:e.target.value})} testId="cr-country" />
            <Field label="Posição procurada" name="position_wanted" value={club.position_wanted} onChange={(e)=>setClub({...club,position_wanted:e.target.value})} testId="cr-position" />
            <Field label="Escalão" name="age_group" value={club.age_group} onChange={(e)=>setClub({...club,age_group:e.target.value})} testId="cr-age" />
            <Field label="Pé dominante pretendido" name="dominant_foot" options={["Direito","Esquerdo","Ambidextro","Indiferente"]} value={club.dominant_foot} onChange={(e)=>setClub({...club,dominant_foot:e.target.value})} testId="cr-foot" />
            <Field label="Prazo da procura" name="deadline" value={club.deadline} onChange={(e)=>setClub({...club,deadline:e.target.value})} testId="cr-deadline" />
            <div className="md:col-span-2">
              <Field label="Características procuradas" type="textarea" name="characteristics" value={club.characteristics} onChange={(e)=>setClub({...club,characteristics:e.target.value})} testId="cr-characteristics" />
            </div>
            <div className="md:col-span-2">
              <Field label="Observações" type="textarea" name="notes" value={club.notes} onChange={(e)=>setClub({...club,notes:e.target.value})} testId="cr-notes" />
            </div>
            <div className="md:col-span-2 pt-4">
              <button data-testid="cr-submit" disabled={loadingClub} className="btn-gold">
                {loadingClub ? "A enviar..." : "Enviar pedido"} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* PROPOSTA DE PARCERIA */}
      <section id="parceria" className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="section-eyebrow mb-6">Formulário</div>
          <h2 className="font-cinzel uppercase text-white text-3xl md:text-5xl tracking-tight mb-12">Proposta de parceria</h2>
          <form data-testid="partnership-form" onSubmit={submitPartner} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Nome" name="name" required value={partnership.name} onChange={(e)=>setPartnership({...partnership,name:e.target.value})} testId="pt-name" />
            <Field label="Organização" name="organization" value={partnership.organization} onChange={(e)=>setPartnership({...partnership,organization:e.target.value})} testId="pt-org" />
            <Field label="Cargo" name="role" value={partnership.role} onChange={(e)=>setPartnership({...partnership,role:e.target.value})} testId="pt-role" />
            <Field label="País" name="country" value={partnership.country} onChange={(e)=>setPartnership({...partnership,country:e.target.value})} testId="pt-country" />
            <Field label="Email" type="email" required name="email" value={partnership.email} onChange={(e)=>setPartnership({...partnership,email:e.target.value})} testId="pt-email" />
            <Field label="Telefone" name="phone" value={partnership.phone} onChange={(e)=>setPartnership({...partnership,phone:e.target.value})} testId="pt-phone" />
            <div className="md:col-span-2">
              <Field label="Tipo de parceria" name="partnership_type" options={["Clube","Academia","Treinador","Departamento de scouting","Agente FIFA","Organização desportiva","Parceiro especializado","Outro"]} value={partnership.partnership_type} onChange={(e)=>setPartnership({...partnership,partnership_type:e.target.value})} testId="pt-type" />
            </div>
            <div className="md:col-span-2">
              <Field label="Descrição da proposta" type="textarea" name="proposal_description" value={partnership.proposal_description} onChange={(e)=>setPartnership({...partnership,proposal_description:e.target.value})} testId="pt-description" />
            </div>
            <div className="md:col-span-2">
              <Field label="Mensagem" type="textarea" name="message" value={partnership.message} onChange={(e)=>setPartnership({...partnership,message:e.target.value})} testId="pt-message" />
            </div>
            <div className="md:col-span-2 pt-4">
              <button data-testid="pt-submit" disabled={loadingPartner} className="btn-gold">
                {loadingPartner ? "A enviar..." : "Enviar proposta"} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
